/* Yo Gorilla Mats size visualizer: to-scale SVG floor plan + room-fit helper.
   Data comes from a JSON script tag rendered from live products/variants (snippets/ygm-size-data). */
(() => {
  const SVGNS = 'http://www.w3.org/2000/svg';
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const ease = (t) => 1 - Math.pow(1 - t, 3);
  const fmt = (n) => (Math.round(n * 10) / 10).toString();

  function el(name, attrs, parent) {
    const n = document.createElementNS(SVGNS, name);
    for (const k in attrs) n.setAttribute(k, attrs[k]);
    if (parent) parent.appendChild(n);
    return n;
  }

  class Viz {
    constructor(root) {
      this.root = root;
      root._viz = this;
      this.data = JSON.parse(root.querySelector('script[type="application/json"]').textContent);
      this.line = root.dataset.line || this.data.line || 'exercise';
      if (!this.data[this.line] || !this.data[this.line].length) this.line = 'exercise';
      this.svg = root.querySelector('svg.ygm-viz__svg');
      this.state = { l: 0, w: 0, vbW: 216, vbH: 108 };
      this.room = null;
      // Measured style (dirC): architect dimension lines + a room planner with movable furniture
      this.measured = root.classList.contains('ygm-viz--measured');
      this.planEl = this.measured ? root.querySelector('[data-plan]') : null;
      this.off = { x: 0, y: 0 };
      this.rot = false;
      this.build();
      this.bind();
      const start = this.pickInitial();
      this.select(start, { animate: false, silent: true, from0: !reduce });
      if (!reduce && 'IntersectionObserver' in window) {
        this.root.classList.add('is-waiting');
        const io = new IntersectionObserver((es) => {
          if (es.some((e) => e.isIntersecting)) { io.disconnect(); this.root.classList.remove('is-waiting'); this.drawIn(); }
        }, { threshold: 0.35 });
        io.observe(this.svg);
      } else {
        this.render(this.target.l * 12, this.target.w * 12);
      }
    }

    sizes() { return this.data[this.line] || []; }

    pick(key) {
      let m = this.sizes().find((s) => `${s.l}x${s.w}` === key);
      if (!m) { const other = this.line === 'exercise' ? 'yoga' : 'exercise'; if ((this.data[other] || []).some((s) => `${s.l}x${s.w}` === key)) { this.setLine(other); m = this.sizes().find((s) => `${s.l}x${s.w}` === key); } }
      if (m) this.select(m, { animate: true, fromUser: true });
    }

    pickInitial() {
      const list = this.sizes();
      const d = this.data;
      if (d.mode === 'size' && d.current) { const m = list.find((s) => `${s.l}x${s.w}` === d.current); if (m) return m; }
      if (d.mode === 'umbrella') {
        const checked = document.querySelector('variant-picker input[type="radio"]:checked[data-variant-id]');
        const m = checked && list.find((s) => String(s.id) === checked.dataset.variantId);
        if (m) return m;
      }
      const want = this.root.dataset.default || '7x5';
      return list.find((s) => `${s.l}x${s.w}` === want) || list[Math.floor(list.length / 2)] || list[0];
    }

    build() {
      const s = this.svg;
      s.innerHTML = '';
      const defs = el('defs', {}, s);
      const pat = el('pattern', { id: this.uid('grid'), width: 12, height: 12, patternUnits: 'userSpaceOnUse' }, defs);
      el('path', { d: 'M 12 0 L 0 0 0 12', fill: 'none', class: 'ygm-viz__gridline' }, pat);
      this.floor = el('rect', { x: -400, y: -400, width: 1200, height: 1200, fill: `url(#${this.uid('grid')})`, class: 'ygm-viz__floor' }, s);
      this.roomRect = el('rect', { class: 'ygm-viz__room', x: 0, y: 0, width: 0, height: 0, rx: 1 }, s);
      this.matG = el('g', { class: 'ygm-viz__matg' }, s);
      this.mat = el('rect', { class: 'ygm-viz__mat', x: 0, y: 0, width: 0, height: 0, pathLength: 100 }, this.matG);
      this.std = el('g', { class: 'ygm-viz__std' }, s);
      // the standard 68 x 24 in yoga mat, labelled inside its own outline
      el('rect', { class: 'ygm-viz__stdrect', x: 0, y: 0, width: 68, height: 24 }, this.std);
      this.stdLabel = el('text', { class: 'ygm-viz__stdlabel', x: 34, y: 11, 'text-anchor': 'middle' }, this.std);
      this.stdLabel.textContent = 'Standard yoga mat';
      const sub = el('text', { class: 'ygm-viz__stdsub', x: 34, y: 18.5, 'text-anchor': 'middle' }, this.std);
      sub.textContent = '68 × 24 in';
      this.lenLabel = el('text', { class: 'ygm-viz__dim', 'text-anchor': 'middle' }, s);
      this.widLabel = el('text', { class: 'ygm-viz__dim', 'text-anchor': 'middle' }, s);
      this.roomLabel = el('text', { class: 'ygm-viz__roomlabel', 'text-anchor': 'end' }, s);
      if (this.measured) this.buildMeasured();
    }

    uid(k) { if (!this._id) this._id = 'v' + Math.random().toString(36).slice(2, 7); return `${this._id}-${k}`; }

    bind() {
      this.root.addEventListener('click', (e) => {
        const b = e.target.closest('[data-size]');
        if (b && this.root.contains(b)) {
          const m = this.sizes().find((s) => `${s.l}x${s.w}` === b.dataset.size);
          if (m) this.select(m, { animate: true, fromUser: true });
        }
        const ln = e.target.closest('[data-line]');
        if (ln && this.root.contains(ln) && ln.dataset.line !== this.line) this.setLine(ln.dataset.line);
      });
      const form = this.root.querySelector('[data-room-form]');
      if (form) {
        const go = (e) => { if (e) e.preventDefault(); this.fit(); };
        form.addEventListener('submit', go);
        form.addEventListener('input', () => { clearTimeout(this._t); this._t = setTimeout(() => this.fit(true), 450); });
      }
      if (this.data.mode === 'umbrella') {
        document.addEventListener('change', (e) => {
          const r = e.target;
          if (!(r instanceof HTMLInputElement) || !r.closest('variant-picker') || !r.dataset.variantId) return;
          const m = this.sizes().find((s) => String(s.id) === r.dataset.variantId);
          if (m && (m.l !== this.target.l || m.w !== this.target.w)) this.select(m, { animate: true, silent: true });
        });
      }
      if (this.planEl) this.bindPlan();
    }

    setLine(line) {
      if (!this.data[line] || !this.data[line].length) return;
      this.line = line;
      this.root.dataset.activeLine = line;
      this.root.querySelectorAll('[data-line]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.line === line)));
      this.root.querySelectorAll('[data-sizes-for]').forEach((g) => { g.hidden = g.dataset.sizesFor !== line; });
      const keep = this.sizes().find((s) => s.l === this.target.l && s.w === this.target.w);
      this.select(keep || this.sizes().find((s) => s.popular) || this.sizes()[0], { animate: true });
      if (this.room) this.fit(true);
    }

    select(size, opts = {}) {
      if (!size) return;
      this.target = size;
      const key = `${size.l}x${size.w}`;
      this.root.querySelectorAll(`[data-sizes-for="${this.line}"] [data-size]`).forEach((b) => {
        const on = b.dataset.size === key;
        b.setAttribute('aria-pressed', String(on));
        b.classList.toggle('is-active', on);
      });
      this.updateText(size);
      if (opts.from0) { this.state.l = 0; this.state.w = 0; return; }
      if (!reduce) { this.mat.classList.remove('is-draw'); this.mat.getBoundingClientRect(); this.mat.classList.add('is-draw'); }
      if (opts.animate && !reduce) this.tween(size.l * 12, size.w * 12); else this.render(size.l * 12, size.w * 12);
      if (opts.fromUser && this.data.mode === 'umbrella' && this.line === this.data.line) this.syncVariant(size);
      if (this.planEl && this.room) this.planStatus();
    }

    syncVariant(size) {
      const input = document.querySelector(`variant-picker input[type="radio"][data-variant-id="${size.id}"]`);
      if (input && !input.checked) input.click();
    }

    updateText(s) {
      const q = (sel) => this.root.querySelector(sel);
      const ratio = (s.l * 12 * s.w * 12) / (this.data.std.l * this.data.std.w);
      const thick = this.thickLabel(s);
      const set = (sel, v) => { const n = q(sel); if (n) n.textContent = v; };
      const sz = q('[data-out="size"]');
      if (sz) sz.innerHTML = `${s.l}<i>×</i>${s.w}`;
      set('[data-out="dims"]', `${s.l * 12} × ${s.w * 12} in · ${s.sqft} sq ft · ${thick}`);
      const ro = q('[data-out="ratio"]');
      if (ro) ro.innerHTML = `${fmt(ratio)}<i>×</i>`;
      set('[data-out="price"]', s.price);
      set('[data-out="best"]', s.best || '');
      const cta = q('[data-out="cta"]');
      if (cta) {
        // the other line's sizes are another product: link to it instead of this page's picker
        if (this.data.mode === 'umbrella' && this.line === this.data.line) {
          cta.setAttribute('href', '#');
          cta.onclick = (e) => { e.preventDefault(); this.syncVariant(s); const btn = document.querySelector('.product-details [name="add"], .product-details button.add-to-cart-button'); (btn || document.querySelector('variant-picker'))?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' }); };
          cta.querySelector('span') && (cta.querySelector('span').textContent = `Choose ${s.l}×${s.w}`);
        } else {
          cta.onclick = null;
          cta.setAttribute('href', s.url);
          cta.querySelector('span') && (cta.querySelector('span').textContent = `Shop the ${s.l}×${s.w}`);
        }
        cta.toggleAttribute('aria-disabled', !s.avail);
      }
      const live = q('[data-out="live"]');
      if (live) live.textContent = `${s.l} by ${s.w} feet, ${s.sqft} square feet, about ${fmt(ratio)} times a standard yoga mat. ${s.price}.`;
    }

    thickLabel(s) {
      const t = (s.t || '').toLowerCase();
      if (t.includes('1/4')) return '1/4 in (6mm) thick';
      if (t.includes('8mm')) return this.line === 'exercise' ? '5/16 in (8mm) thick' : '8mm thick';
      return s.t || '';
    }

    tween(l, w) {
      cancelAnimationFrame(this._raf);
      const a = { l: this.state.l, w: this.state.w }, t0 = performance.now(), dur = 520;
      const step = (now) => {
        const k = ease(Math.min(1, (now - t0) / dur));
        this.render(a.l + (l - a.l) * k, a.w + (w - a.w) * k);
        if (k < 1) this._raf = requestAnimationFrame(step);
      };
      this._raf = requestAnimationFrame(step);
    }

    drawIn() {
      this.root.classList.add('is-drawing');
      this.tween(this.target.l * 12, this.target.w * 12);
      setTimeout(() => this.root.classList.remove('is-drawing'), 900);
    }

    render(L, W) {
      if (this.measured) return this.renderMeasured(L, W);
      this.state.l = L; this.state.w = W;
      const roomL = this.room ? this.room.l * 12 : 0, roomW = this.room ? this.room.w * 12 : 0;
      const pad = 16;
      const spanL = Math.max(15 * 12, L, roomL), spanW = Math.max(6 * 12, W, roomW);
      const vbW = spanL + pad * 2, vbH = spanW + pad * 2 + 8;
      this.svg.setAttribute('viewBox', `${-pad} ${-pad - 8} ${vbW} ${vbH}`);
      this.mat.setAttribute('width', Math.max(0, L)); this.mat.setAttribute('height', Math.max(0, W));
      // standard mat sits inside the big mat, bottom-left, with an inset
      const sx = Math.max(1, Math.min(6, (L - 68) / 2)), sy = Math.max(4, W - 24 - 6);
      this.std.setAttribute('transform', `translate(${sx} ${sy})`);
      this.std.style.opacity = W > 30 ? 1 : 0;
      this.lenLabel.setAttribute('x', L / 2); this.lenLabel.setAttribute('y', -5);
      this.lenLabel.textContent = L > 1 ? `${fmt(L / 12)} ft` : '';
      this.widLabel.setAttribute('transform', `translate(${L + 6} ${W / 2}) rotate(90)`);
      this.widLabel.textContent = W > 1 ? `${fmt(W / 12)} ft` : '';
      if (this.room) {
        this.roomRect.setAttribute('width', roomL); this.roomRect.setAttribute('height', roomW);
        this.roomLabel.setAttribute('x', roomL - 2); this.roomLabel.setAttribute('y', roomW + 9);
        this.roomLabel.textContent = `Your space ${fmt(this.room.l)} × ${fmt(this.room.w)} ft`;
        const fits = this.fitsIn(this.target, this.room);
        this.root.classList.toggle('is-nofit', !fits);
      } else {
        this.roomRect.setAttribute('width', 0); this.roomLabel.textContent = '';
        this.root.classList.remove('is-nofit');
      }
    }

    /* ---------- Measured style: dimension lines + room planner ---------- */
    buildMeasured() {
      const s = this.svg, defs = s.querySelector('defs');
      const major = el('pattern', { id: this.uid('major'), width: 60, height: 60, patternUnits: 'userSpaceOnUse' }, defs);
      el('path', { d: 'M 60 0 L 0 0 0 60', fill: 'none', class: 'ygm-viz__gridline ygm-viz__gridline--major' }, major);
      const hatch = el('pattern', { id: this.uid('hatch'), width: 4, height: 4, patternUnits: 'userSpaceOnUse', patternTransform: 'rotate(45)' }, defs);
      el('line', { x1: 0, y1: 0, x2: 0, y2: 4, class: 'ygm-viz__hatch' }, hatch);
      const floor2 = el('rect', { x: -400, y: -400, width: 1200, height: 1200, fill: `url(#${this.uid('major')})`, class: 'ygm-viz__floor' });
      s.insertBefore(floor2, this.roomRect);
      this.furnG = el('g', { class: 'ygm-viz__furn' });
      s.insertBefore(this.furnG, this.matG);
      this.hatch = el('rect', { class: 'ygm-viz__mathatch', x: 0, y: 0, width: 0, height: 0, fill: `url(#${this.uid('hatch')})` }, this.matG);
      this.matG.dataset.drag = 'mat';
      this.dlL = el('path', { class: 'ygm-viz__dl' }, s);
      this.dlW = el('path', { class: 'ygm-viz__dl' }, s);
      s.appendChild(this.lenLabel); s.appendChild(this.widLabel);
      this.furn = {
        sofa: { w: 84, h: 36, label: 'Sofa', on: true, x: 0, y: 0 },
        tv: { w: 60, h: 18, label: 'TV stand', on: true, x: 0, y: 0 },
        door: { w: 36, h: 36, label: 'Door', on: true, x: 0, y: 0 },
      };
      for (const k in this.furn) {
        const f = this.furn[k];
        f.g = el('g', { class: `ygm-viz__piece ygm-viz__piece--${k}`, 'data-drag': k, role: 'button', 'aria-roledescription': 'movable', 'aria-label': `${f.label}. Arrow keys move it.` }, this.furnG);
        if (k === 'door') {
          el('rect', { class: 'ygm-viz__piecehit', x: 0, y: 0, width: f.w, height: f.h }, f.g);
          el('path', { class: 'ygm-viz__doorswing', d: `M0 0 V${f.h} A${f.w} ${f.h} 0 0 0 ${f.w} 0` }, f.g);
          el('path', { class: 'ygm-viz__doorleaf', d: `M0 0 V${f.h}` }, f.g);
          const t = el('text', { class: 'ygm-viz__piecelabel', x: 15, y: 13, 'text-anchor': 'middle' }, f.g); t.textContent = f.label;
        } else {
          el('rect', { class: 'ygm-viz__piecebox', x: 0, y: 0, width: f.w, height: f.h }, f.g);
          const t = el('text', { class: 'ygm-viz__piecelabel', x: f.w / 2, y: f.h / 2, 'text-anchor': 'middle' }, f.g); t.textContent = f.label;
        }
      }
      this.drawFurn(); // hidden until a room is entered
    }

    // half the gap a dimension label needs in its line, in user units (measured once per text and breakpoint)
    labelGap(t) {
      const txt = t.textContent;
      if (!txt) return 0;
      const key = `${txt}|${window.innerWidth >= 750}`;
      this._gap = this._gap || {};
      if (!this._gap[key]) {
        let w = 0;
        try { w = t.getComputedTextLength(); } catch (_) { /* not rendered yet */ }
        if (!w) { const fs = parseFloat(getComputedStyle(t).fontSize) || 5; w = txt.length * fs * 0.62; }
        else this._gap[key] = w / 2 + 2.2;
        return w / 2 + 2.2;
      }
      return this._gap[key];
    }

    // drawn mat size in inches for the current orientation
    matDims(L, W) { return this.rot ? { ml: W, mw: L } : { ml: L, mw: W }; }

    clampBox(x, y, w, h) {
      if (!this.room) return { x: 0, y: 0 };
      const RL = this.room.l * 12, RW = this.room.w * 12, snap = (v) => Math.round(v / 6) * 6;
      x = w <= RL ? Math.min(Math.max(0, snap(x)), RL - w) : 0;
      y = h <= RW ? Math.min(Math.max(0, snap(y)), RW - h) : 0;
      return { x, y };
    }

    renderMeasured(L, W) {
      this.state.l = L; this.state.w = W;
      const room = this.room, roomL = room ? room.l * 12 : 0, roomW = room ? room.w * 12 : 0;
      const { ml, mw } = this.matDims(L, W);
      const pad = 18, spanL = Math.max(15 * 12, ml, roomL), spanW = Math.max(6 * 12, mw, roomW);
      this.svg.setAttribute('viewBox', `${-pad} ${-pad - 4} ${spanL + pad * 2 + 4} ${spanW + pad * 2 + 10}`);
      let ox = 0, oy = 0;
      if (room) { const b = this.clampBox(this.off.x, this.off.y, ml, mw); ox = b.x; oy = b.y; this.off = b; }
      this.matG.setAttribute('transform', `translate(${ox} ${oy})`);
      for (const r of [this.mat, this.hatch]) { r.setAttribute('width', Math.max(0, ml)); r.setAttribute('height', Math.max(0, mw)); }
      const sx = Math.max(1, Math.min(6, (ml - 68) / 2)), sy = Math.max(4, mw - 24 - 6);
      this.std.setAttribute('transform', `translate(${ox + sx} ${oy + sy})`);
      this.std.style.opacity = !room && mw > 30 && ml >= 70 ? 1 : 0;
      // architect dimension lines: extension lines, a dimension line with oblique ticks, the label in a gap on the line
      const y0 = oy - 8, x0 = ox + ml + 8, t = 2.2, cx = ox + ml / 2, cy = oy + mw / 2;
      this.lenLabel.setAttribute('x', cx); this.lenLabel.setAttribute('y', y0);
      this.lenLabel.textContent = ml > 1 ? `${fmt(ml / 12)} ft` : '';
      this.widLabel.setAttribute('transform', `translate(${x0} ${cy}) rotate(90)`);
      this.widLabel.textContent = mw > 1 ? `${fmt(mw / 12)} ft` : '';
      // the dimension line breaks around its label (drawn whole if the label would not fit)
      const gl = this.labelGap(this.lenLabel), gw = this.labelGap(this.widLabel);
      const lineL = gl * 2 < ml - 6 ? `M${ox - 3} ${y0}H${cx - gl}M${cx + gl} ${y0}H${ox + ml + 3}` : `M${ox - 3} ${y0}H${ox + ml + 3}`;
      const lineW = gw * 2 < mw - 6 ? `M${x0} ${oy - 3}V${cy - gw}M${x0} ${cy + gw}V${oy + mw + 3}` : `M${x0} ${oy - 3}V${oy + mw + 3}`;
      this.dlL.setAttribute('d', ml > 1 ? `M${ox} ${oy - 2}V${y0 - 3}M${ox + ml} ${oy - 2}V${y0 - 3}${lineL}M${ox - t} ${y0 + t}L${ox + t} ${y0 - t}M${ox + ml - t} ${y0 + t}L${ox + ml + t} ${y0 - t}` : '');
      this.dlW.setAttribute('d', mw > 1 ? `M${ox + ml + 2} ${oy}H${x0 + 3}M${ox + ml + 2} ${oy + mw}H${x0 + 3}${lineW}M${x0 - t} ${oy + t}L${x0 + t} ${oy - t}M${x0 - t} ${oy + mw + t}L${x0 + t} ${oy + mw - t}` : '');
      if (room) {
        this.roomRect.setAttribute('width', roomL); this.roomRect.setAttribute('height', roomW);
        this.roomLabel.setAttribute('x', roomL); this.roomLabel.setAttribute('y', roomW + 10);
        this.roomLabel.textContent = `Room ${fmt(room.l)} × ${fmt(room.w)} ft`;
        this.root.classList.toggle('is-nofit', !this.fitsIn(this.target, room));
        this.root.classList.toggle('is-clash', this.overlaps({ x: ox, y: oy, w: ml, h: mw }).length > 0);
      } else {
        this.roomRect.setAttribute('width', 0); this.roomLabel.textContent = '';
        this.root.classList.remove('is-nofit', 'is-clash');
      }
    }

    drawFurn() {
      for (const k in this.furn) {
        const f = this.furn[k], show = f.on && !!this.room;
        f.g.setAttribute('transform', `translate(${f.x} ${f.y})`);
        f.g.style.display = show ? '' : 'none';
        if (show) f.g.setAttribute('tabindex', '0'); else f.g.removeAttribute('tabindex');
      }
    }

    placeFurniture() {
      const RL = this.room.l * 12, RW = this.room.w * 12, F = this.furn, snap = (v) => Math.max(0, Math.round(v / 6) * 6);
      F.tv.x = snap((RL - F.tv.w) / 2); F.tv.y = 0;
      F.sofa.x = snap((RL - F.sofa.w) / 2); F.sofa.y = Math.max(0, RW - F.sofa.h);
      F.door.x = 0; F.door.y = 0;
    }

    overlaps(b) {
      if (!this.furn || !this.room) return [];
      const hit = [];
      for (const k in this.furn) {
        const f = this.furn[k];
        if (f.on && b.x < f.x + f.w && f.x < b.x + b.w && b.y < f.y + f.h && f.y < b.y + b.h) hit.push(k === 'door' ? 'door swing' : f.label.toLowerCase());
      }
      return hit;
    }

    // closest-to-centre spot on a 6 in grid where a w x h mat clears the furniture, or null
    clearSpot(w, h) {
      const RL = this.room.l * 12, RW = this.room.w * 12;
      if (w > RL || h > RW) return null;
      let best = null, bd = Infinity;
      for (let x = 0; x <= RL - w; x += 6) for (let y = 0; y <= RW - h; y += 6) {
        if (this.overlaps({ x, y, w, h }).length) continue;
        const d = Math.hypot(x + w / 2 - RL / 2, y + h / 2 - RW / 2);
        if (d < bd) { bd = d; best = { x, y }; }
      }
      return best;
    }

    autoPlace() {
      const { ml, mw } = this.matDims(this.target.l * 12, this.target.w * 12);
      const RL = this.room.l * 12, RW = this.room.w * 12;
      this.off = this.clearSpot(ml, mw) || this.clampBox((RL - ml) / 2, (RW - mw) / 2, ml, mw);
    }

    largestClear() {
      const list = this.sizes().slice().sort((a, b) => b.sqft - a.sqft || b.l - a.l);
      for (const s of list) if (this.clearSpot(s.l * 12, s.w * 12) || this.clearSpot(s.w * 12, s.l * 12)) return s;
      return null;
    }

    planSync() {
      const on = !!this.room;
      this.planEl.hidden = !on;
      if (on) {
        const key = `${this.room.l}x${this.room.w}`;
        if (this._roomKey !== key) { this._roomKey = key; this.placeFurniture(); }
        this.autoPlace();
        this.matG.setAttribute('tabindex', '0');
        this.matG.setAttribute('role', 'button');
        this.matG.setAttribute('aria-roledescription', 'movable');
        this.matG.setAttribute('aria-label', 'Your mat. Arrow keys move it, R turns it.');
      } else {
        this._roomKey = null; this.off = { x: 0, y: 0 }; this.rot = false;
        ['tabindex', 'role', 'aria-roledescription', 'aria-label'].forEach((a) => this.matG.removeAttribute(a));
      }
      this.drawFurn();
      this.planStatus();
    }

    planStatus() {
      const out = this.root.querySelector('[data-out="plan"]');
      if (!out || !this.room) return;
      const s = this.target, name = `${s.l}×${s.w}`;
      const { ml, mw } = this.matDims(s.l * 12, s.w * 12);
      const RL = this.room.l * 12, RW = this.room.w * 12;
      const anyFurn = Object.values(this.furn).some((f) => f.on);
      let msg;
      if (ml > RL || mw > RW) {
        msg = (mw <= RL && ml <= RW) ? `The ${name} fits this room if you turn it 90°.` : `The ${name} is bigger than this room.`;
      } else {
        const b = this.clampBox(this.off.x, this.off.y, ml, mw);
        const hit = this.overlaps({ x: b.x, y: b.y, w: ml, h: mw });
        if (!anyFurn) msg = `The ${name} fits. Drag it to see the open floor around it.`;
        else if (!hit.length) msg = `The ${name} is clear of the furniture. Drag any piece to try your own layout.`;
        else {
          const list = hit.length > 1 ? `${hit.slice(0, -1).join(', ')} and ${hit[hit.length - 1]}` : hit[0];
          msg = `The ${name} overlaps the ${list}. `;
          if (this.clearSpot(ml, mw) || this.clearSpot(mw, ml)) msg += 'Drag it to a clear spot, or move the furniture.';
          else {
            const lc = this.largestClear();
            msg += lc ? `The largest size clear of this furniture is the ${lc.l}×${lc.w}.` : 'Move or remove a piece to make room.';
          }
        }
      }
      out.textContent = msg;
    }

    movePiece(id, x, y) {
      if (id === 'mat') {
        const { ml, mw } = this.matDims(this.state.l, this.state.w);
        this.off = this.clampBox(x, y, ml, mw);
      } else {
        const f = this.furn[id], RL = this.room.l * 12, RW = this.room.w * 12, snap = (v) => Math.round(v / 6) * 6;
        f.x = f.w <= RL ? Math.min(Math.max(0, snap(x)), RL - f.w) : 0;
        f.y = f.h <= RW ? Math.min(Math.max(0, snap(y)), RW - f.h) : 0;
        this.drawFurn();
      }
      this.render(this.state.l, this.state.w);
    }

    piecePos(id) { return id === 'mat' ? this.off : { x: this.furn[id].x, y: this.furn[id].y }; }

    svgPt(e) {
      const m = this.svg.getScreenCTM();
      if (!m) return { x: 0, y: 0 };
      const p = new DOMPoint(e.clientX, e.clientY).matrixTransform(m.inverse());
      return { x: p.x, y: p.y };
    }

    bindPlan() {
      const svg = this.svg;
      svg.addEventListener('pointerdown', (e) => {
        const g = e.target.closest('[data-drag]');
        if (!g || !this.room || (e.pointerType === 'mouse' && e.button !== 0)) return;
        e.preventDefault();
        const id = g.dataset.drag, p = this.svgPt(e), o = this.piecePos(id);
        this.drag = { id, g, dx: p.x - o.x, dy: p.y - o.y, pid: e.pointerId };
        try { g.setPointerCapture(e.pointerId); } catch (_) { /* capture is optional */ }
        g.classList.add('is-drag');
        if (g.focus) g.focus({ preventScroll: true });
      });
      svg.addEventListener('pointermove', (e) => {
        if (!this.drag || e.pointerId !== this.drag.pid) return;
        const p = this.svgPt(e);
        this.movePiece(this.drag.id, p.x - this.drag.dx, p.y - this.drag.dy);
      });
      const end = (e) => {
        if (!this.drag || e.pointerId !== this.drag.pid) return;
        this.drag.g.classList.remove('is-drag');
        this.drag = null;
        this.planStatus();
      };
      svg.addEventListener('pointerup', end);
      svg.addEventListener('pointercancel', end);
      svg.addEventListener('keydown', (e) => {
        const g = e.target.closest && e.target.closest('[data-drag]');
        if (!g || !this.room) return;
        const id = g.dataset.drag, step = e.shiftKey ? 24 : 6, o = this.piecePos(id);
        const d = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[e.key];
        if (d) {
          e.preventDefault();
          this.movePiece(id, o.x + d[0], o.y + d[1]);
          clearTimeout(this._kt); this._kt = setTimeout(() => this.planStatus(), 250);
        } else if (id === 'mat' && (e.key === 'r' || e.key === 'R')) { e.preventDefault(); this.turn(); }
      });
      this.planEl.addEventListener('click', (e) => {
        const fb = e.target.closest('[data-furn]');
        if (fb) {
          const f = this.furn[fb.dataset.furn];
          f.on = !f.on; fb.setAttribute('aria-pressed', String(f.on));
          this.drawFurn(); this.render(this.state.l, this.state.w); this.planStatus();
        }
        if (e.target.closest('[data-rotate]')) this.turn();
      });
    }

    turn() {
      this.rot = !this.rot;
      this.render(this.state.l, this.state.w);
      this.planStatus();
    }

    fitsIn(s, r) { return (s.l <= r.l && s.w <= r.w) || (s.l <= r.w && s.w <= r.l); }

    fit(quiet) {
      const form = this.root.querySelector('[data-room-form]');
      const out = this.root.querySelector('[data-out="fit"]');
      const a = parseFloat(form.querySelector('[name="room_l"]').value), b = parseFloat(form.querySelector('[name="room_w"]').value);
      if (!(a > 0 && b > 0)) {
        if (!quiet && out) out.textContent = 'Enter the length and width of your open floor in feet.';
        this.room = null;
        this.root.querySelectorAll('[data-size]').forEach((btn) => { btn.classList.remove('is-fit', 'is-nofit'); delete btn.dataset.fit; });
        this.root.classList.remove('has-room');
        if (this.planEl) this.planSync();
        this.render(this.state.l, this.state.w);
        return;
      }
      this.root.classList.add('has-room');
      this.room = { l: Math.max(a, b), w: Math.min(a, b) };
      const fits = this.sizes().filter((s) => this.fitsIn(s, this.room));
      this.root.querySelectorAll(`[data-sizes-for="${this.line}"] [data-size]`).forEach((btn) => {
        const s = this.sizes().find((x) => `${x.l}x${x.w}` === btn.dataset.size);
        btn.classList.toggle('is-fit', !!s && fits.includes(s));
        btn.classList.toggle('is-nofit', !!s && !fits.includes(s));
        if (s) btn.dataset.fit = fits.includes(s) ? 'Fits' : 'Too big'; else delete btn.dataset.fit;
      });
      if (!fits.length) {
        if (out) out.textContent = `None of our mats fit ${fmt(this.room.l)} × ${fmt(this.room.w)} ft. The smallest is 6 × 4 ft.`;
        if (this.planEl) this.planSync();
        this.render(this.state.l, this.state.w);
        return;
      }
      const best = fits.slice().sort((x, y) => y.sqft - x.sqft || y.l - x.l)[0];
      if (out) out.innerHTML = `Fits your space: <strong>${fits.map((s) => `${s.l}×${s.w}`).join(', ')}</strong>. Largest fit: <strong>${best.l}×${best.w} ft</strong>.`;
      this.select(best, { animate: true, fromUser: false });
      if (this.planEl) this.planSync();
      this.render(this.state.l, this.state.w);
    }
  }

  const init = () => document.querySelectorAll('[data-ygm-viz]:not([data-ready])').forEach((r) => { r.dataset.ready = '1'; try { new Viz(r); } catch (e) { console.warn('ygm-viz', e); } });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
  // Size numerals elsewhere on the page (data-viz-pick="9x6") jump to the visualizer and select that size.
  document.addEventListener('click', (e) => {
    const a = e.target.closest('[data-viz-pick]');
    if (!a) return;
    const root = document.querySelector('[data-ygm-viz]');
    if (!root || !root._viz) return;
    e.preventDefault();
    root._viz.pick(a.dataset.vizPick);
    root.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
  });
})();
